import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import {
  CfcAttestAuthorizeCallout,
  CfcAttestAuthorizeTitle,
  CfcAttestAuthorizeLead,
  CfcAttestAuthorizeCheckboxGroup,
  CfcAttestAuthorizeCheckboxLabel,
  CfcAttestAuthorizeSignatureLabel,
  CfcAttestAuthorizeWarning,
  CfcAttestAuthorizeItalic,
  CfcAttestAuthorizeLegalText,
  CfcCollectionDatePanel,
  CfcCollectionDateField,
  CfcCheckbox,
} from "./CfcStepAttestAndAuthorize.styles";
import type { CfcFormData } from "./types";
import { formatWithTimezoneOffset } from "../../utils/helpers";
import {
  getCfcCollectionDateMaximum,
  isCfcCollectionDateAfterDeadline,
} from "./cfcCollectionDate";

interface CfcStepAttestAndAuthorizeProps {
  cfcEligibleCollectByDate: string;
  formData: CfcFormData;
  isComplete: boolean;
  onFormChange: (updates: Partial<CfcFormData>) => void;
  providerName: string;
  providerNpi: string;
}

const ONLY_UNITY_ANEUPLOIDY_LABEL = (
  <>
    to the best of my knowledge, <strong>Unity Aneuploidy</strong> (cfDNA) is
    the <strong>only</strong> cell-free DNA aneuploidy screening test performed
    for this patient during the current pregnancy.
    <CfcAttestAuthorizeWarning>
      I understand that if available records indicate otherwise, this patient
      will be deemed ineligible for Unity Confirm and the test will be
      cancelled.
    </CfcAttestAuthorizeWarning>
  </>
);

const SINGLETON_PREGNANCY_LABEL =
  "this is a singleton pregnancy, and that this pregnancy was not achieved with an egg donor or gestational carrier.";

const PROVIDER_AUTHORIZATION_COPY = (
  <>
    By submission of this requisition and accompanying sample, I hereby
    authorize and direct BillionToOne to (1) utilize the above information to
    process the indicated test for this patient and (2) release the results and
    patient information to the patient&apos;s third-party payer, as needed. I
    certify (1) all information provided herein is true and accurate, (2) I am
    authorized by law to request the test, (3) the test is reasonable and
    medically necessary for the treatment and management of this patient, (4)
    the patient has been counseled on the potential results, benefits and
    limitations of the test, and (5) I have obtained informed consent to the
    extent required under applicable law. I agree to provide the necessary
    information and medical records to BillionToOne needed to submit and process
    claims to payers.
  </>
);

const getCollectByDeadlineLabel = (
  formattedDeadline: string,
): React.ReactElement => (
  <>
    this patient&apos;s blood sample will be drawn on or before{" "}
    <strong>{formattedDeadline}</strong>.
    <CfcAttestAuthorizeItalic>
      Samples collected after this date will be rejected, due to the low
      likelihood of capturing intact fetal cells past 15w6d gestation.
    </CfcAttestAuthorizeItalic>
  </>
);

export const CfcStepAttestAndAuthorize = ({
  cfcEligibleCollectByDate,
  formData,
  isComplete,
  onFormChange,
  providerName,
  providerNpi,
}: CfcStepAttestAndAuthorizeProps): React.ReactElement => {
  const formattedDeadline =
    formatWithTimezoneOffset(cfcEligibleCollectByDate) || "the indicated date";
  const collectionDateMin = new Date().toLocaleDateString("en-CA");
  const collectionDateMax = getCfcCollectionDateMaximum(
    cfcEligibleCollectByDate,
  );
  const hasCollectionDateError = isCfcCollectionDateAfterDeadline(
    formData.collectionDate,
    cfcEligibleCollectByDate,
  );
  const signatureParts = [
    providerName,
    providerNpi ? `NPI: ${providerNpi}` : "",
  ]
    .filter(Boolean)
    .join(" - ");
  const signatureLabel = signatureParts
    ? `Signed and submitted by ordering provider: ${signatureParts}`
    : "Signed and submitted by ordering provider";

  return (
    <CfcAttestAuthorizeCallout $isComplete={isComplete}>
      <CfcAttestAuthorizeTitle>
        Please read carefully and confirm below
      </CfcAttestAuthorizeTitle>
      <CfcAttestAuthorizeLead>
        As the patient&apos;s healthcare provider, or an authorized individual
        on their behalf, I confirm that:
      </CfcAttestAuthorizeLead>
      <CfcAttestAuthorizeCheckboxGroup>
        <CfcAttestAuthorizeCheckboxLabel
          control={
            <CfcCheckbox
              checked={formData.attestation1}
              onChange={(e) => onFormChange({ attestation1: e.target.checked })}
            />
          }
          label={ONLY_UNITY_ANEUPLOIDY_LABEL}
        />
        <CfcAttestAuthorizeCheckboxLabel
          control={
            <CfcCheckbox
              checked={formData.attestation2}
              onChange={(e) => onFormChange({ attestation2: e.target.checked })}
            />
          }
          label={SINGLETON_PREGNANCY_LABEL}
        />
        <CfcAttestAuthorizeCheckboxLabel
          control={
            <CfcCheckbox
              checked={formData.attestation3}
              onChange={(e) => onFormChange({ attestation3: e.target.checked })}
            />
          }
          label={getCollectByDeadlineLabel(formattedDeadline)}
        />
      </CfcAttestAuthorizeCheckboxGroup>

      <CfcCollectionDatePanel>
        <CfcCollectionDateField
          label="Collection date (if known)"
          id="cfc-order-collection-date"
          variant="standard"
          type="date"
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: collectionDateMin,
            ...(collectionDateMax ? { max: collectionDateMax } : {}),
          }}
          error={hasCollectionDateError}
          helperText={
            hasCollectionDateError ? (
              <>
                <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden />
                Collection date must be on or before {formattedDeadline}.
              </>
            ) : undefined
          }
          value={formData.collectionDate}
          onChange={(event) =>
            onFormChange({ collectionDate: event.target.value })
          }
        />
      </CfcCollectionDatePanel>

      <CfcAttestAuthorizeLegalText>
        {PROVIDER_AUTHORIZATION_COPY}
      </CfcAttestAuthorizeLegalText>
      <CfcAttestAuthorizeSignatureLabel
        control={
          <CfcCheckbox
            checked={formData.providerAuthorization}
            onChange={(e) =>
              onFormChange({ providerAuthorization: e.target.checked })
            }
          />
        }
        label={signatureLabel}
      />
    </CfcAttestAuthorizeCallout>
  );
};
